// ============ PARLOV EARNINGS-GAP RESEARCH — analysis page block ============
// nopoly.ai's 31-year study of abnormal opening gaps on S&P 500 + Nasdaq-100
// names, served through our proxy at /api/nopoly?feed=parlov.
//
// Laid out the way the source publishes it: the study's own description, then
// each question in order (Q1 weekday, Q2 movers raw and robust, Q3 gap fill,
// Q4 the strategy table) with the upstream `howToRead` line as its subtitle,
// so a reader gets the author's framing rather than ours. Everything is open —
// no toggle — because a statistics table hidden behind a click is a statistics
// table nobody reads.
import React, { useEffect, useState } from 'react';
import { SecHead, Verdict } from './core';
import { fetchFundamentals, type Fundamental } from '../services/api';
import { useT } from '../lib/i18n';

interface Weekday { day: string; n: number; avg_abn_gap: number; avg_raw_gap?: number; }
interface Mover { symbol: string; n: number; avg_abn_gap: number; max_gap: number; }
interface Bucket { bucket: string; n: number; avg_2wk: number; winrate_pct: number; }
interface NoFill { symbol: string; n: number; nofill_rate_pct: number; fill_rate_pct?: number; }
interface Strat { n?: number; avg_pct?: number; avg_cont_pct?: number; winrate_pct?: number; }
interface Parlov {
  title?: string; description?: string; howToRead?: Record<string, string>;
  generated?: string; n_events?: number; n_symbols?: number; date_range?: [string, string];
  gap_floor_pct?: number;
  Q1_weekday?: Weekday[];
  Q2_stock_biggest_avg_gap?: Mover[];
  Q2_robust?: Mover[];
  Q3_note?: string; Q3_overall_fill_rate?: number; Q3_overall_nofill_rate?: number; Q3_top10_nofill?: NoFill[];
  Q4_buydip_by_size?: Bucket[];
  Q4_strategy?: Record<string, Strat>;
}

const pct = (v?: number, d = 2) => (typeof v === 'number' ? `${v.toFixed(d)}%` : '—');
const num = (v?: number | null, d = 2) => (typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: d }) : '—');

// The strategy block is keyed by an internal name; these are the readable
// versions, in the order that tells the story: hold vs fade, up vs down.
const STRAT_ROWS: [string, string, string][] = [
  ['long_2wk_gapdown_hold', 'ซื้อ gap-down ถือ 2 สัปดาห์', 'buy gap-down, hold 2 weeks'],
  ['long_2wk_gapup_hold', 'ซื้อ gap-up ถือ 2 สัปดาห์', 'buy gap-up, hold 2 weeks'],
  ['long_1wk_gapdown_hold', 'ซื้อ gap-down ถือ 1 สัปดาห์', 'buy gap-down, hold 1 week'],
  ['long_1wk_gapup_hold', 'ซื้อ gap-up ถือ 1 สัปดาห์', 'buy gap-up, hold 1 week'],
  ['sameday_o2c_gapdown', 'gap-down เปิด→ปิด วันเดียว', 'gap-down, same-day open→close'],
  ['sameday_o2c_gapup', 'gap-up เปิด→ปิด วันเดียว', 'gap-up, same-day open→close'],
  ['long_2wk_fade', 'สวนทาง (fade) ถือ 2 สัปดาห์', 'fade the gap, hold 2 weeks'],
  ['long_2wk_momentum', 'ตามแรง (momentum) ถือ 2 สัปดาห์', 'follow the gap, hold 2 weeks'],
];

interface ScorePart { k: string; th: string; en: string; v: number }
interface Screen {
  symbol: string;
  verdict: 'buy' | 'sell' | 'neutral';
  technical?: { score: number; rsi?: number; ma50?: number; ma200?: number; parts: ScorePart[] } | null;
  fundamental?: { score: number; period?: string | null; revenueGrowth?: number | null; epsGrowth?: number | null; operatingMargin?: number | null; parts: ScorePart[] } | null;
}

export function ParlovGapBlock() {
  const t = useT();
  const [p, setP] = useState<Parlov | null>(null);
  const [stale, setStale] = useState(false);
  const [failed, setFailed] = useState(false);
  const [funds, setFunds] = useState<Record<string, Fundamental>>({});
  const [screen, setScreen] = useState<Record<string, Screen>>({});

  useEffect(() => {
    let alive = true;
    fetch('/api/nopoly?feed=parlov')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => { if (alive) { setP(v.data); setStale(!!v.stale); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const movers = (p?.Q2_robust || []).slice(0, 12);
  useEffect(() => {
    if (!movers.length) return;
    const syms = movers.map(m => m.symbol);
    let alive = true;
    // Trailing price/PE/EPS/yield for the columns, and the technical +
    // latest-quarter screen for the verdict. The screen is one batched call.
    fetchFundamentals(syms).then(f => { if (alive) setFunds(x => ({ ...x, ...f })); });
    fetch(`/api/screen?symbols=${encodeURIComponent(syms.join(','))}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => { if (alive) setScreen(v); })
      .catch(() => {});
    return () => { alive = false; };
  }, [p]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed || !p) return null;

  const how = p.howToRead || {};
  const scope = [
    p.date_range ? `${p.date_range[0]} → ${p.date_range[1]}` : '',
    p.n_symbols ? `${p.n_symbols} ${t('หลักทรัพย์', 'symbols')}` : '',
    p.n_events ? `${p.n_events.toLocaleString()} ${t('เหตุการณ์', 'events')}` : '',
    p.gap_floor_pct ? `${t('เกณฑ์ gap', 'gap floor')} ≥${p.gap_floor_pct}%` : '',
  ].filter(Boolean).join(' · ');

  const s = p.Q4_strategy || {};
  const head = s.long_2wk_gapdown_hold;

  return (
    <div className="mm-card glass mm-parlov">
      <SecHead th="สถิติ Gap หลังงบ (Parlov · 31 ปี)" en="PARLOV EARNINGS-GAP STUDY · 31 YEARS"></SecHead>

      <div className="mm-mlearn-meta mono dim">
        {scope && <span>{scope}</span>}
        {stale && <span>· {t('ข้อมูลสำรอง (ต้นทางไม่ตอบ)', 'snapshot — upstream unreachable')}</span>}
      </div>

      {p.description && <p className="mm-parlov-desc dim">{p.description}</p>}

      {/* The headline edge, stated the way the study states it. */}
      {head && (
        <div className="mm-parlov-head">
          <span className="mm-mlearn-key mono">{t('แก่น', 'EDGE')}</span>
          <p>
            {t('ซื้อที่ราคาเปิดของวันที่ gap ลง แล้วถือ 2 สัปดาห์ ให้ผลตอบแทนเฉลี่ย', 'Buying the gap-down open and holding two weeks returns')}{' '}
            <b className="mono up">{pct(head.avg_pct)}</b>{' '}
            {t('ชนะ', 'winning')} <b className="mono">{pct(head.winrate_pct, 1)}</b>{' '}
            {t('จาก', 'across')} <b className="mono">{head.n?.toLocaleString()}</b> {t('เหตุการณ์', 'events')} —{' '}
            {t('ดีกว่าการไล่ตาม gap ขึ้น และดีกว่าการสวนทันทีในวันเดียว', 'better than chasing the gap up, and better than fading it the same day')}
          </p>
        </div>
      )}

      <div className="mm-parlov-grid">
        <Section title={t('Q1 · ขนาด gap เฉลี่ยแยกตามวัน', 'Q1 · average abnormal gap by weekday')} note={how.Q1_weekday}>
          <Table head={[t('วัน', 'Day'), 'n', t('gap ผิดปกติ', 'abnormal'), t('gap ดิบ', 'raw')]}
            rows={(p.Q1_weekday || []).map(r => [r.day, num(r.n, 0), pct(r.avg_abn_gap), pct(r.avg_raw_gap)])}></Table>
        </Section>

        <Section title={t('Q3 · การปิด gap ภายใน 4 ชม.', 'Q3 · gap fill within 4 hours')} note={how.Q3}>
          <p className="mm-parlov-line">
            {t('ปิดได้', 'filled')} <b className="mono">{pct(p.Q3_overall_fill_rate, 1)}</b> ·{' '}
            {t('ไม่ปิด', 'did not fill')} <b className="mono down">{pct(p.Q3_overall_nofill_rate, 1)}</b>
            {' — '}{t('อย่าฟันสวน gap โดยอัตโนมัติ', 'do not blind-fade a gap')}
          </p>
          <Table head={[t('ไม่ปิด gap บ่อยสุด', 'least likely to fill'), 'n', t('ไม่ปิด', 'no-fill')]}
            rows={(p.Q3_top10_nofill || []).map(r => [r.symbol, String(r.n), pct(r.nofill_rate_pct, 1)])}></Table>
          {p.Q3_note && <p className="mm-footnote dim">{p.Q3_note}</p>}
        </Section>

        <Section title={t('Q4 · ซื้อ gap-down ถือ 2 สัปดาห์ แยกตามขนาด gap', 'Q4 · buy the gap-down, hold 2 weeks — by gap size')} note={how.Q4_buydip_by_size}>
          <Table head={[t('ช่วง gap', 'bucket'), 'n', t('ผลตอบแทน', 'return'), t('ชนะ', 'win')]}
            rows={(p.Q4_buydip_by_size || []).map(r => [r.bucket, num(r.n, 0), pct(r.avg_2wk), pct(r.winrate_pct, 1)])}></Table>
        </Section>

        <Section title={t('Q4 · ตารางกลยุทธ์ทั้งหมด', 'Q4 · the full strategy table')} note={how.Q4_strategy}>
          <Table head={[t('กลยุทธ์', 'strategy'), 'n', t('ผลตอบแทน', 'return'), t('ชนะ', 'win')]}
            rows={STRAT_ROWS.filter(([k]) => s[k]).map(([k, th, enLbl]) => {
              const r = s[k];
              const v = r.avg_pct ?? r.avg_cont_pct;
              return [t(th, enLbl), r.n ? r.n.toLocaleString() : '—', pct(v), pct(r.winrate_pct, 1)];
            })}
            emphasise={0}></Table>
        </Section>

        <Section title={t('Q2 · หุ้นที่ gap แรงสุด (ชุดดิบ)', 'Q2 · biggest average gap (raw ranking)')} note={how.Q2_stock_biggest_avg_gap}>
          <Table head={[t('หุ้น', 'symbol'), 'n', t('เฉลี่ย', 'avg'), t('สูงสุด', 'max')]}
            rows={(p.Q2_stock_biggest_avg_gap || []).slice(0, 15).map(r => [r.symbol, String(r.n), pct(r.avg_abn_gap), pct(r.max_gap)])}></Table>
        </Section>

        <div className="mm-parlov-tbl">
          <span className="mm-std-lbl mono">{t('Q2 · ชุด robust (n 40–280) + งบการเงินและมุมมองรายตัว', 'Q2 · robust list (n 40–280) with fundamentals and a per-stock read')}</span>
          {how.Q2_robust && <p className="mm-parlov-note dim">{how.Q2_robust}</p>}
          <div className="mm-tblwrap">
            <table className="mm-table">
              <thead>
                <tr>
                  <th>{t('หุ้น', 'Symbol')}</th><th>{t('gap เฉลี่ย', 'avg gap')}</th><th>{t('ราคา', 'Price')}</th>
                  <th>P/E</th><th>{t('งบไตรมาสล่าสุด', 'latest quarter')}</th><th>{t('เทคนิคอล', 'technical')}</th><th>{t('มุมมอง', 'Read')}</th>
                </tr>
              </thead>
              <tbody>
                {movers.map(r => {
                  const f = funds[r.symbol];
                  const sc = screen[r.symbol];
                  const side = (parts?: ScorePart[]) => (parts || []).map(x => t(x.th, x.en)).join(' · ');
                  return (
                    <tr key={r.symbol}>
                      <td><b>{r.symbol}</b></td>
                      <td className="mono">{pct(r.avg_abn_gap)}</td>
                      <td className="mono">{num(f?.price)}</td>
                      <td className="mono">{num(f?.pe, 1)}</td>
                      <td className="mm-parlov-cell">
                        {sc?.fundamental
                          ? <React.Fragment>
                              <b className="mono">{sc.fundamental.period || ''}</b>
                              <em className="dim">{side(sc.fundamental.parts)}</em>
                            </React.Fragment>
                          : <span className="dim mono">{sc ? t('ไม่มีงบ', 'no filing') : '…'}</span>}
                      </td>
                      <td className="mm-parlov-cell">
                        {sc?.technical
                          ? <em className="dim">{side(sc.technical.parts)}</em>
                          : <span className="dim mono">…</span>}
                      </td>
                      <td>
                        {sc
                          ? <span className="mm-parlov-read">
                              <Verdict v={sc.verdict}></Verdict>
                              <em className="mono dim">
                                {t('เทคนิค', 'tech')} {sc.technical ? (sc.technical.score >= 0 ? '+' : '') + sc.technical.score : '—'}
                                {' · '}{t('งบ', 'fund')} {sc.fundamental ? (sc.fundamental.score >= 0 ? '+' : '') + sc.fundamental.score : '—'}
                              </em>
                            </span>
                          : <span className="dim mono">…</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mm-footnote dim">
            {t('ให้คะแนนสองด้านแยกกัน — เทคนิคอล: เหนือ/ต่ำกว่า MA50 และ MA200 ±1 · RSI เกิน 70 −1 ต่ำกว่า 30 +1 · โมเมนตัม 20 วันเกิน ±5% ±1 · ห่างจุดสูงสุด 52 สัปดาห์ ไม่เกิน 5% +1 เกิน 25% −1 · งบไตรมาสล่าสุด: รายได้และกำไร YoY เกิน 10% +1 ติดลบ −1 · มาร์จิ้นดำเนินงานเกิน 15% +1 ติดลบ −1 · ไตรมาสมีกำไร +1 ขาดทุน −2',
              'The two sides are scored separately — technical: above/below the 50- and 200-day ±1 · RSI over 70 −1, under 30 +1 · 20-day momentum beyond ±5% ±1 · within 5% of the 52-week high +1, more than 25% below −1 · latest quarter: revenue and earnings YoY over 10% +1, negative −1 · operating margin over 15% +1, negative −1 · profitable quarter +1, loss −2')}
            {' · '}
            {t('ซื้อ ต้องได้ทั้งสองด้าน (เทคนิค ≥+2 และ งบ ≥+2) · ขาย ต้องเสียทั้งสองด้าน · ถ้าขาดด้านใดด้านหนึ่งจะไม่ขึ้นซื้อเด็ดขาด',
              'BUY needs both sides (technical ≥+2 and fundamentals ≥+2) · SELL needs both against · with either side missing it never reads BUY')}
            {' · '}{t('ราคาและงบจาก Yahoo · เป็นการคัดกรองเชิงกล ไม่ใช่คำแนะนำรายบุคคล', 'prices and filings via Yahoo · a mechanical screen, not personal advice')}
          </p>
        </div>
      </div>

      <p className="mm-footnote dim">
        {t('งานวิจัยจาก nopoly.ai (Parlov earnings-gap study)', 'Research by nopoly.ai (Parlov earnings-gap study)')}
        {p.generated ? ` · ${t('ประมวลผล', 'generated')} ${p.generated.slice(0, 10)}` : ''}
        {' · '}{t('ผลในอดีตไม่รับประกันอนาคต · เพื่อการศึกษา ไม่ใช่คำแนะนำการลงทุน', 'past performance ≠ future results · education only, not investment advice')}
      </p>
    </div>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="mm-parlov-tbl">
      <span className="mm-std-lbl mono">{title}</span>
      {note && <p className="mm-parlov-note dim">{note}</p>}
      {children}
    </div>
  );
}

function Table({ head, rows, emphasise }: { head: string[]; rows: string[][]; emphasise?: number }) {
  if (!rows.length) return null;
  return (
    <div className="mm-tblwrap">
      <table className="mm-table">
        <thead><tr>{head.map(h => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i === emphasise ? 'mm-parlov-hi' : ''}>
              {r.map((c, j) => <td key={j} className={j === 0 ? '' : 'mono'}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
